import * as Sentry from '@sentry/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'

export const Route = createFileRoute('/user/verify-email')({
  validateSearch: z.object({
    email: z.string().optional().catch(''),
    token: z.string().optional().catch('')
  }),
  beforeLoad: ({ search }) => {
    if (!search.email || !search.token) {
      throw redirect({ to: '/user/resend-verification' })
    }
  },
  head: () => ({ meta: [{ title: 'Verify Email - Snowvault' }] }),
  component: VerifyEmail
})

type PageState = 'verifying' | 'success' | 'error'

function VerifyEmail() {
  const { email, token } = Route.useSearch()
  const [pageState, setPageState] = useState<PageState>('verifying')

  useEffect(() => {
    async function verify() {
      try {
        const params = new URLSearchParams({ email: email ?? '', token: token ?? '' })
        const res = await fetch(`${window.location.origin}/api/user/email/verify?${params}`, {
          credentials: 'same-origin'
        })
        if (!res.ok) {
          setPageState('error')
        } else {
          setPageState('success')
        }
      } catch (err) {
        Sentry.captureException(err)
        setPageState('error')
      }
    }
    void verify()
  }, [email, token])

  if (pageState === 'success') {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Email verified</CardTitle>
              <CardDescription>Your email address has been successfully verified.</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login" className="text-sm underline underline-offset-4 hover:no-underline">
                Sign in to continue
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Verification failed</CardTitle>
              <CardDescription>Your verification link is invalid or has expired.</CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to="/user/resend-verification"
                search={{ email: email ?? '', error: '' }}
                className="text-sm underline underline-offset-4 hover:no-underline"
              >
                Request a new verification link
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <Card className="gap-4">
          <CardHeader>
            <CardTitle>Verifying your email</CardTitle>
            <CardDescription>Please wait while we verify your email address...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    </div>
  )
}
